import { useState, useContext } from 'react';
import { ThemeContext } from './ThemeContext.jsx';

// ─── Content Consent System ───────────────────────────────────────────────────
//
// My Fate, My Love is an adult creative fiction platform. The story universe
// explores dark, emotionally intense, and morally complex themes as a creative
// outlet. This consent modal gates those permissions explicitly.
//
// Key design decisions:
// • One-time agreement, versioned (bump CONSENT_VERSION to force re-acceptance)
// • Stored in localStorage — not Supabase, so it survives sessions without DB calls
// • Legal protections and child character safety are hardcoded in the AI system
//   prompt regardless of consent — they cannot be consented away
// ─────────────────────────────────────────────────────────────────────────────

export const CONSENT_VERSION = 'v1';
export const CONSENT_KEY = `mfml_content_consent_${CONSENT_VERSION}`;

export function hasConsented() {
  try { return localStorage.getItem(CONSENT_KEY) === 'accepted'; } catch { return false; }
}

export function recordConsent() {
  try { localStorage.setItem(CONSENT_KEY, 'accepted'); } catch {}
}


// ─── Modal Component ──────────────────────────────────────────────────────────

export default function ContentConsentModal({ onAccept, onDecline }) {
  const { theme: _t } = useContext(ThemeContext);
  const [checked, setChecked] = useState(false);

  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.92)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '20px',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
  };

  const panel = {
    background: 'var(--ds-glass, rgba(21,11,13,0.77))',
    border: '1px solid var(--ds-glass-edge, #c4a8823d)',
    borderRadius: 'var(--ds-r-xl, 28px)',
    maxWidth: '560px', width: '100%',
    maxHeight: '90vh', overflowY: 'auto',
    padding: '32px 36px',
    color: 'var(--ds-ink, #cdbbb4)',
    boxShadow: 'var(--ds-shadow-3), inset 0 1px 0 var(--ds-glass-light, rgba(255,255,255,0.055))',
    backdropFilter: 'blur(14px) saturate(1.4)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
  };

  const heading = {
    fontFamily: "'Cinzel', serif",
    fontSize: '20px',
    color: 'var(--ds-gold, #c4a882)',
    letterSpacing: '0.08em',
    marginBottom: '6px',
  };

  const sub = {
    fontSize: '13px',
    color: 'var(--ds-ink-low, #9c8983)',
    marginBottom: '24px',
    fontStyle: 'italic',
  };

  const sectionTitle = {
    fontFamily: "'Cinzel', serif",
    fontSize: '11px',
    color: 'var(--ds-gold, #c4a882)',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '8px',
    marginTop: '20px',
    display: 'block',
    fontWeight: '600',
  };

  const itemList = {
    margin: '0 0 4px',
    paddingLeft: '16px',
    fontSize: '13px',
    color: 'var(--ds-ink, #cdbbb4)',
    lineHeight: '1.7',
  };

  const hardLimit = {
    margin: '0 0 4px',
    paddingLeft: '16px',
    fontSize: '13px',
    color: 'var(--ds-danger, #d6485e)',
    lineHeight: '1.7',
  };

  const divider = {
    border: 'none',
    borderTop: '1px solid var(--ds-line-soft, #2a161b)',
    margin: '20px 0',
  };

  const checkRow = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginTop: '24px',
    padding: '14px 16px',
    background: 'var(--ds-gold-a0f, rgba(196,168,130,0.06))',
    border: '1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))',
    borderRadius: 'var(--ds-r-md, 14px)',
    cursor: 'pointer',
  };

  const acceptBtn = {
    width: '100%', marginTop: '16px',
    padding: '13px',
    borderRadius: 'var(--ds-r-pill, 999px)',
    border: '1px solid var(--ds-glass-edge, #c4a8823d)',
    background: checked ? 'linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))' : 'var(--ds-surface-2, #1d1013)',
    color: checked ? 'var(--ds-ink-hi, #f1e7e3)' : 'var(--ds-ink-low, #9c8983)',
    fontFamily: "'Cinzel', serif",
    fontSize: '13px',
    letterSpacing: '1px',
    fontWeight: 700,
    cursor: checked ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s',
    boxShadow: checked ? 'var(--ds-glow-gold, 0 0 20px -4px #c4a88259)' : 'none',
  };

  const declineBtn = {
    width: '100%', marginTop: '8px',
    padding: '10px',
    borderRadius: 'var(--ds-r-pill, 999px)',
    border: '1px solid var(--ds-glass-edge, #c4a8823d)',
    background: 'transparent',
    color: 'var(--ds-ink-low, #9c8983)',
    fontSize: '12px',
    cursor: 'pointer',
  };

  return (
    <div style={overlay}>
      <div style={panel}>

        <div style={heading}>My Fate, My Love — RP</div>
        <div style={sub}>Content Agreement · Adult Creative Fiction Platform</div>

        <p style={{ fontSize: '13px', color: 'var(--ds-ink, #cdbbb4)', lineHeight: '1.7', margin: '0 0 4px' }}>
          This is an adult collaborative fiction platform. The story universe of MFML is intentionally dark, emotionally intense, and morally complex — that is the creative outlet it was designed to be.
        </p>

        <span style={sectionTitle}>This platform contains</span>
        <ul style={itemList}>
          <li>Dark and toxic relationship dynamics written as fiction</li>
          <li>Emotional manipulation, possessiveness, obsession, and volatile conflict between characters</li>
          <li>Morally grey and morally bad characters behaving in character — including cruelty, coercion, and harm to others within the story</li>
          <li>Characters being pushed past their presented limits — breaking, cracking, showing what's beneath the surface</li>
          <li>Explicit emotional content: grief, rage, jealousy, obsession, despair, and desire in full</li>
          <li>Violence, power imbalances, and consequences that are not softened or resolved neatly</li>
          <li>Mature romantic and intimate fiction between adult characters</li>
        </ul>

        <span style={{
          fontFamily: "'Cinzel', serif",
          fontSize: '13px',
          color: 'var(--ds-danger, #d6485e)',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          marginBottom: '10px',
          marginTop: '24px',
          display: 'block',
          borderLeft: '3px solid var(--ds-danger-a44, #d6485e44)',
          paddingLeft: '10px',
          lineHeight: '1.4',
        }}>What this platform does not contain — ever</span>
        <ul style={hardLimit}>
          <li>Sexual content involving characters written and presented as minors — content follows age tiers: 15 and under is innocent/cutesy only; 16–17 permits fade-to-black; 18+ is unrestricted. These tiers are based on how a character is written and presented, not any supernatural age claim.</li>
          <li>Real-world instructions for causing harm</li>
          <li>Content that crosses from fiction into actual illegal material</li>
        </ul>

        <hr style={divider} />

        <p style={{ fontSize: '12px', color: 'var(--ds-ink-low, #9c8983)', lineHeight: '1.6', margin: 0 }}>
          The fiction written here is not an endorsement of any behavior depicted. Dark fiction is not harmful fiction. The characters, relationships, and events exist in a story — not as instructions, ideals, or reality.
        </p>

        <div style={checkRow} onClick={() => setChecked(c => !c)}>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => setChecked(c => !c)}
            onClick={e => e.stopPropagation()}
            style={{ marginTop: '2px', accentColor: 'var(--ds-gold, #c4a882)', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', outline: 'none' }}
          />
          <span style={{ fontSize: '13px', color: 'var(--ds-ink, #cdbbb4)', lineHeight: '1.5' }}>
            I am 18 or older. I understand this platform contains dark adult fiction and I consent to that content as a creative reader and writer. I understand the limits above are absolute and non-negotiable.
          </span>
        </div>

        <button
          style={acceptBtn}
          disabled={!checked}
          onClick={() => { if (checked) { recordConsent(); onAccept(); } }}
        >
          Enter — I Agree
        </button>

        <button style={declineBtn} onClick={onDecline}>
          Leave — this isn't for me
        </button>

      </div>
    </div>
  );
}
